// ENG-10039: how to read the result of the LEGACY `brew --cask claude-code`
// path, extracted so it can be tested without importing manager-worker.
//
// Same reason `claude-code-upgrade-throttle.ts` was split out under ENG-6418:
// manager-worker is a 13k-line import graph, evaluating it inside a test's
// budget flakes under parallel load, so the load-bearing judgement lives here
// and manager-worker calls it.
//
// WHAT WENT WRONG, and it is why this is a module rather than two inline
// conditionals. On every host provisioned since Claude Code moved to
// `npm install -g @anthropic-ai/claude-code` (host-bootstrap.ts), there is no
// Homebrew cask. The manager ran `brew upgrade --cask claude-code` anyway,
// because its only precondition was "is there a `claude` on PATH" — and there
// is one, at `/usr/bin/claude`, put there by npm. So the upgrade failed with
// `Error: Cask 'claude-code' is not installed.` every day, on 7 of 10 hosts
// measured, oldest at least 9 days (the full retained-log window on agt-aws-1:
// 9 attempts, 9 failures, zero successes).
//
// It was invisible because it is only a log line: no alert kind covers it.

/**
 * What `brew list --cask claude-code` actually told us.
 *
 * THREE STATES, NOT TWO, and the third is the one that matters (CodeRabbit on
 * #5616). `runAsync` RESOLVES with a non-zero exit code rather than rejecting,
 * and Homebrew uses non-zero exits for operational errors too — a network
 * failure, a lock, a broken tap. Mapping every non-zero code to `absent` would
 * read a transient brew error as "this host has no cask", skip the upgrade, and
 * because the throttle marker is stamped BEFORE the probe, suppress any retry
 * for ~24h. On a genuine cask host that is a silent missed upgrade, which is
 * the same invisible-skip shape this whole ticket exists to remove.
 *
 * So `absent` is claimed ONLY on brew's specific not-installed message.
 * Everything else non-zero is `unknown`: skip this cycle, say so, try again on
 * the next in-window poll.
 */
export type CaskPresence = 'present' | 'absent' | 'unknown';

/**
 * Brew's wording for a cask that is not installed, e.g.
 *   Error: Cask 'claude-code' is not installed.
 * Matched loosely on the stable half of the sentence rather than the whole
 * line, because the cask name is interpolated and brew has moved the
 * surrounding punctuation before.
 */
const NOT_INSTALLED_RE = /is not installed/i;

export function caskPresenceFromListResult(r: {
  code: number;
  stdout: string;
  stderr: string;
}): CaskPresence {
  if (r.code === 0) return 'present';
  return NOT_INSTALLED_RE.test(`${r.stdout}\n${r.stderr}`) ? 'absent' : 'unknown';
}

export type BrewUpgradeVerdict = 'upgraded' | 'already-current' | 'failed';

/**
 * Classify a `brew upgrade --cask claude-code` run.
 *
 * THE NON-ZERO-BUT-FINE CASE IS REAL, not defensive padding: brew exits
 * non-zero while printing `already installed` / `up-to-date` / `not upgraded`
 * for a cask that needs no work. Treating that as a failure would have made
 * this monitor cry wolf on exactly the hosts where nothing is wrong, and a
 * version check that cries wolf gets muted — after which it is worth less than
 * nothing, because everyone now believes the case is covered.
 *
 * Both streams are searched because brew is inconsistent about which one
 * carries the notice, and the auto-update banner interleaves them.
 */
export function classifyBrewUpgradeResult(r: {
  code: number;
  stdout: string;
  stderr: string;
}): BrewUpgradeVerdict {
  const combined = `${r.stdout}\n${r.stderr}`;
  const noWorkNeeded =
    combined.includes('already installed') ||
    combined.includes('up-to-date') ||
    combined.includes('not upgraded');

  if (r.code === 0) return noWorkNeeded ? 'already-current' : 'upgraded';
  return noWorkNeeded ? 'already-current' : 'failed';
}
